import { useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ConversationList, {
  Conversation,
} from '@/components/Chat/ConversationList'
import WelcomePage from '@/components/Chat/WelcomePage'
import { useChatStore } from '@/stores/chatStore'
import { useSessionCache } from '@/hooks/useSessionCache'
import {
  useAgentDetail,
  useAgentDetailWithSearchConfig,
} from '@/api/hooks/useAgent'
import { useRecentAgentsStore } from '@/stores/recentAgentsStore'
// import { generateUUIDFromTimestamp } from '@/lib/utils'
import type { ChatSession as ChatSessionType } from '@/api/types/chat.type'

// 将API返回的ChatSession转换为组件需要的Conversation格式
const convertSessionToConversation = (
  session: ChatSessionType
): Conversation => ({
  id: session.sessionId,
  title: session.title,
  lastMessage: '', // 这里可以后续从消息记录中获取
  timestamp: new Date(session.updatedAt),
})

/**
 * 聊天欢迎页面组件
 * 专门处理聊天欢迎页面，包含侧边栏和欢迎内容
 */
const ChatWelcomePage = () => {
  const navigate = useNavigate()
  const { id: agentId } = useParams<{ id: string }>()
  // const { currentUser } = useUserStore()
  const sessionCache = useSessionCache()
  const { sessions, addSession, removeSession, updateSession } = useChatStore()
  const { addRecentAgent } = useRecentAgentsStore()

  // 获取智能体详情
  const {
    data: agentDetail,
    isLoading: agentLoading,
    error: agentError,
  } = useAgentDetail(agentId)

  // 获取智能体知识库配置
  const { searchConfig, isLoading: searchConfigLoading } =
    useAgentDetailWithSearchConfig(agentDetail)

  // 获取用户ID
  // const userId = useMemo(
  //   () => currentUser?.id?.toString() || '123456',
  //   [currentUser?.id]
  // )

  // 转换会话列表格式
  const conversations = useMemo(
    () => sessions.map(convertSessionToConversation),
    [sessions]
  )

  // 定期清理过期缓存
  useEffect(() => {
    const interval = setInterval(() => {
      sessionCache.clearExpiredCache()
    }, 5 * 60 * 1000) // 每5分钟清理一次

    return () => clearInterval(interval)
  }, [sessionCache])

  // 处理会话选择
  const handleSelectConversation = (conversationId: string) => {
    console.log('handleSelectConversation called:', conversationId)

    // 根据是否有智能体ID决定跳转路径
    const targetPath = agentId
      ? `/agents/chat/s/${conversationId}?id=${agentId}`
      : `/chat/s/${conversationId}`

    navigate(targetPath)
  }

  // 处理开始新聊天
  const handleStartChat = async (newSessionId: string, userMessage: string) => {
    console.log('handleStartChat called:', { newSessionId, userMessage })

    // 如果有智能体详情，添加到近期使用列表
    if (agentDetail) {
      addRecentAgent(agentDetail)
    }

    // 将初始消息存储到sessionStorage中
    sessionStorage.setItem(`initial_message_${newSessionId}`, userMessage)

    // 如果有searchConfig，也存储到sessionStorage中
    if (searchConfig) {
      sessionStorage.setItem(
        `search_config_${newSessionId}`,
        JSON.stringify(searchConfig)
      )
    }

    // 创建新的会话对象并添加到store
    const sessionTitle =
      userMessage.slice(0, 20) + (userMessage.length > 20 ? '...' : '')
    const newSession: ChatSessionType = {
      sessionId: newSessionId,
      title: sessionTitle,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    addSession(newSession)

    // 根据是否有智能体ID决定跳转路径
    const chatUrl = agentId
      ? `/agents/chat/s/${newSessionId}?id=${agentId}`
      : `/chat/s/${newSessionId}`
    navigate(chatUrl)
  }

  // 如果是智能体聊天页面，显示加载状态或错误
  if (agentId) {
    if (agentLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-gray-300 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600 text-sm">正在加载智能体信息...</p>
          </div>
        </div>
      )
    }

    if (agentError) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 text-sm">加载智能体信息失败</p>
            <button
              onClick={() => navigate('/agents/list')}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              返回智能体列表
            </button>
          </div>
        </div>
      )
    }

    if (!agentDetail) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 text-sm">未找到智能体信息</p>
            <button
              onClick={() => navigate('/agents/list')}
              className="mt-2 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              返回智能体列表
            </button>
          </div>
        </div>
      )
    }
  }

  // 创建新对话
  const handleNewConversation = async () => {
    console.log('handleNewConversation called')
    // 欢迎页面不需要特殊处理，用户可以直接在欢迎页面开始新对话
  }

  // 删除对话
  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // 从store中删除会话
      removeSession(conversationId)

      // 清除被删除会话的缓存
      sessionCache.clearSessionCache(conversationId)

      console.log('删除对话成功:', conversationId)
    } catch (error) {
      console.error('删除对话失败:', error)
    }
  }

  // 重命名对话
  const handleRenameConversation = async (
    conversationId: string,
    newTitle: string
  ) => {
    try {
      // 更新store中的会话
      updateSession(conversationId, newTitle)

      console.log('重命名对话:', conversationId, '新标题:', newTitle)
    } catch (error) {
      console.error('重命名对话失败:', error)
    }
  }

  return (
    <div className="flex h-full">
      {/* 侧边栏 - 对话列表 */}
      <ConversationList
        conversations={conversations}
        activeConversationId=""
        onSelectConversation={handleSelectConversation}
        onNewConversation={handleNewConversation}
        onDeleteConversation={handleDeleteConversation}
        onRenameConversation={handleRenameConversation}
      />

      {/* 主内容区 - 欢迎页面 */}
      <div className="flex-1 flex flex-col h-full">
        <WelcomePage
          onStartChat={handleStartChat}
          agentDetail={agentDetail}
          isSearchConfigReady={!searchConfigLoading && !!searchConfig}
        />
      </div>
    </div>
  )
}

export default ChatWelcomePage
